import { decodeTokenClaims, getAccessToken, tokenIdentity } from './access-token';
import { clearJarvisSession } from './auth-session';
import { IMPERSONATED_USER_INFO_KEY, clearImpersonationState, hasImpersonationState } from './impersonation-state';

// The access token is the source of truth for who the user is and whether
// they are impersonating. authData, nomor_ppjk and the impersonation keys are
// display caches: when they disagree with the token they are cleared, never
// trusted (next-jarvis#618 §6).

/** `/auth?session=inconsistent` tells the login page why the user is there. */
export const SESSION_INCONSISTENT_PARAM = 'session';
export const SESSION_INCONSISTENT_VALUE = 'inconsistent';

export type ReconcileResult = 'noToken' | 'ok' | 'healed' | 'signedOut';

const readStoredId = (key: string): string | undefined => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    const id = (JSON.parse(raw) as { id?: unknown } | null)?.id;
    return id === undefined || id === null || id === '' ? undefined : String(id);
  } catch {
    return undefined;
  }
};

const signOutInconsistent = (): ReconcileResult => {
  clearJarvisSession();
  clearImpersonationState();
  window.location.href = `/auth?${SESSION_INCONSISTENT_PARAM}=${SESSION_INCONSISTENT_VALUE}`;
  return 'signedOut';
};

/**
 * Brings the localStorage caches in line with the access token. Runs once
 * before the app renders.
 *
 * | token                         | storage                                      | result                 |
 * |-------------------------------|----------------------------------------------|------------------------|
 * | none                          | –                                            | noToken (auth gate)    |
 * | not impersonating             | impersonation keys present                   | keys cleared (healed)  |
 * | impersonating user X          | impersonatedUserInfo.id = authData.id = X    | ok                     |
 * | impersonating user X          | either missing or another id                 | signed out → /auth     |
 * | not impersonating user X      | authData.id present and ≠ X                  | signed out → /auth     |
 *
 * A missing authData.id is not a mismatch for a normal session: not every
 * login path stores it.
 */
export const reconcileSession = (): ReconcileResult => {
  const claims = decodeTokenClaims(getAccessToken());
  if (!claims) return 'noToken';

  const tokenUserId = claims.user_id === undefined || claims.user_id === null ? undefined : String(claims.user_id);
  const authDataId = readStoredId('authData');

  if (claims.impersonated === true) {
    const impersonatedId = readStoredId(IMPERSONATED_USER_INFO_KEY);
    if (!tokenUserId || impersonatedId !== tokenUserId || authDataId !== tokenUserId) {
      return signOutInconsistent();
    }
    return 'ok';
  }

  let result: ReconcileResult = 'ok';
  if (hasImpersonationState()) {
    clearImpersonationState();
    result = 'healed';
  }
  if (tokenUserId && authDataId !== undefined && authDataId !== tokenUserId) {
    return signOutInconsistent();
  }
  return result;
};

/**
 * Whether a cross-tab change of `accessToken` means another account (or
 * another impersonation) is now active. A routine refresh keeps the identity
 * and must not reload every open tab.
 */
export const isIdentityChange = (oldToken: string | null, newToken: string | null): boolean =>
  tokenIdentity(decodeTokenClaims(oldToken)) !== tokenIdentity(decodeTokenClaims(newToken));

/**
 * Reloads this tab when another tab starts, stops or switches an impersonation,
 * signs in or signs out, so this tab's in-memory state (React Query cache,
 * PPJK state) never pairs one account's token with another's data. The reload
 * runs reconcileSession on boot; the destructive branches are not run here,
 * where a half-written start in the other tab could look inconsistent.
 */
export const watchSessionAcrossTabs = () => {
  window.addEventListener('storage', (event) => {
    if (event.storageArea !== localStorage) return;
    // key === null: the other tab cleared all of localStorage.
    if (event.key !== null && event.key !== 'accessToken') return;
    if (event.key === null || isIdentityChange(event.oldValue, event.newValue)) {
      window.location.reload();
    }
  });
};
